<template>
  <div class="login-illustration">
    <!-- Background blur -->
    <img class="illustration-blur" :src="imgs.blur" alt="" aria-hidden="true" />

    <!-- Concentric ring background -->
    <div class="illustration-rings">
      <div v-for="i in 6" :key="i" :class="['ring', `ring--${i}`]" />
    </div>

    <!-- Feature cards — positions from Figma Default variant (650 × 832 ref frame) -->
    <img class="illustration-card ic--ai"            :src="imgs.ai"            alt="" />
    <img class="illustration-card ic--snap-ai"       :src="imgs.snapAi"        alt="" />
    <img class="illustration-card ic--nocode"        :src="imgs.nocode"        alt="" />
    <img class="illustration-card ic--collaboration" :src="imgs.collaboration" alt="" />
    <img class="illustration-card ic--phone"         :src="imgs.phone"         alt="" />
    <img class="illustration-card ic--no-setup"      :src="imgs.noSetup"       alt="" />
    <img class="illustration-card ic--automation"    :src="imgs.automation"    alt="" />
    <img class="illustration-card ic--audit"         :src="imgs.audit"         alt="" />
    <img class="illustration-card ic--appstore"      :src="imgs.appStore"      alt="" />
    <img class="illustration-card ic--playstore"     :src="imgs.playStore"     alt="" />

    <!-- Centre logo circle -->
    <div class="illustration-logo">
      <div class="illustration-logo__circle">
        <img v-if="imgs.companyLogo" :src="imgs.companyLogo" class="illustration-logo__img" alt="inistate" />
      </div>
    </div>
  </div>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';
import { createSpring } from '../commons/springEasing';

import imgAi            from '../../assets/images/login/ai.png';
import imgSnapAi        from '../../assets/images/login/snap_ai.png';
import imgNocode        from '../../assets/images/login/nocode.png';
import imgCollaboration from '../../assets/images/login/collaboration.png';
import imgPhone         from '../../assets/images/login/phone_desktop_link.png';
import imgNoSetup       from '../../assets/images/login/no_setup_tax.png';
import imgAutomation    from '../../assets/images/login/automation.png';
import imgAudit         from '../../assets/images/login/audit_trail.png';
import imgAppStore      from '../../assets/images/brand/app-store-black.svg';
import imgPlayStore     from '../../assets/images/brand/google-play-badge.svg';
import imgCompanyLogo from '../../assets/images/logo/inistate-logo-with-text-transparent.svg';
import imgBlur       from '../../assets/images/login/illus-blur.webp';

export default {
  name: 'login-illustration',
  data() {
    return {
      imgs: {
        ai: imgAi,
        snapAi: imgSnapAi,
        nocode: imgNocode,
        collaboration: imgCollaboration,
        phone: imgPhone,
        noSetup: imgNoSetup,
        automation: imgAutomation,
        audit: imgAudit,
        appStore: imgAppStore,
        playStore: imgPlayStore,
        companyLogo: imgCompanyLogo,
        blur: imgBlur,
      },
    };
  },
  mounted() {
    this.$nextTick(this._animateCards);
  },
  beforeDestroy() {
    (this._cardAnimations || []).forEach(a => a.cancel());
  },
  methods: {
    // Figma spring: Mass 1 · Stiffness 22.7 · Damping 2.07 · Delay 100ms
    _animateCards() {
      let [durMs, ease] = createSpring({ mass: 1, stiffness: 22.7, damping: 2.07 });
      durMs += 250;
      const STEPS  = 60;
      const panelW = window.innerWidth * 0.5;
      const panelH = window.innerHeight;

      // [class, sx vw, sy vh] — offsets from Figma Variant2 cluster to Default position
      const CARDS = [
        ['ic--ai',             13.08,   33.65],
        ['ic--snap-ai',        15.15,   16.59],
        ['ic--nocode',         -2.54,   29.45],
        ['ic--collaboration', -10.15,   15.74],
        ['ic--phone',          19.08,    3.73],
        ['ic--no-setup',       16.92,  -16.83],
        ['ic--automation',      4.46,  -28.73],
        ['ic--audit',          -7.08,  -16.83],
        ['ic--appstore',      -12.85,   -1.92],
        ['ic--playstore',     -13.31,   -7.57],
      ];

      // ── Cards ──────────────────────────────────────────────────────────────
      const cardAnims = CARDS.map(([cls, sxVw, syVh]) => {
        const el = this.$el.querySelector(`.${cls}`);
        if (!el) return null;

        const sxPx = (sxVw / 100) * panelW;
        const syPx = (syVh / 100) * panelH;

        const keyframes = Array.from({ length: STEPS + 1 }, (_, i) => {
          const p = ease(i / STEPS);
          return {
            transform: `translate(${sxPx * (1 - p)}px, ${syPx * (1 - p)}px) scale(${0.55 + 0.45 * p})`,
            opacity: Math.min(1, i / 4),
          };
        });

        return el.animate(keyframes, { duration: durMs, delay: 250, fill: 'forwards', easing: 'linear' });
      }).filter(Boolean);

      // ── Rings ───────────────────────────────────────────────────────────────
      // Spring shrink: scale(1) → scale(0).
      // fill:'forwards' holds the collapsed state so rings stay hidden after settling.
      const ringKf = Array.from({ length: STEPS + 1 }, (_, i) => {
        const p = ease(i / STEPS);
        return { transform: `translate(-50%, -50%) scale(${Math.max(0, 1 - p)})` };
      });

      const ringAnims = Array.from(
        this.$el.querySelectorAll('.ring'),
        ring => ring.animate(ringKf, { duration: durMs, delay: 250, fill: 'forwards', easing: 'linear' })
      );

      this._cardAnimations = [...cardAnims, ...ringAnims];
    },
  },
};
</script>

<style lang="scss" scoped>
// ─────────────────────────────────────────────────────────────
// Container
// ─────────────────────────────────────────────────────────────
.login-illustration {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: var(--inistate-color-primary-50, #f1f9fc);
}

.illustration-blur {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  z-index: 0;
}

// ─────────────────────────────────────────────────────────────
// Rings
// ─────────────────────────────────────────────────────────────
.illustration-rings {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
}

.ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  aspect-ratio: 1;
}

// Sizes — 3px solid white per Figma
.ring--1 { width: 22%;  border: 3px solid #fff; }
.ring--2 { width: 38%;  border: 3px solid #fff; }
.ring--3 { width: 57%;  border: 3px solid #fff; }
.ring--4 { width: 80%;  border: 3px solid #fff; }
.ring--5 { width: 108%; border: 3px solid #fff; }
.ring--6 { width: 140%; border: 3px solid #fff; }

// ─────────────────────────────────────────────────────────────
// Feature cards
// Positions derived from Figma Default variant (650 × 832 ref)
// left%  = x_px / 650 * 100
// top%   = y_px / 832 * 100
// width% = w_px / 650 * 100
// ─────────────────────────────────────────────────────────────
.illustration-card {
  position: absolute;
  height: auto;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  z-index: 2;
  filter: drop-shadow(0 4px 12px rgba(0,0,0,0.08));
  opacity: 0; /* WAAPI animates from here */
}

// Final positions from Figma Default variant (650 × 832 ref).
// Spring animation applied via WAAPI in mounted() — see _animateCards().
// Scatter offsets (vw/vh from Variant2 cluster) live in the JS CARDS config.

.ic--ai            { left:15.23%; top:10.70%; width:204px; }
.ic--snap-ai       { left:12.46%; top:28.00%; width:210px; }
.ic--nocode        { left:46.46%; top:15.99%; width:205px; }
.ic--collaboration { left:60.92%; top:30.53%; width:223px; }
.ic--phone         { left: 4.62%; top:40.38%; width:179px; }
.ic--no-setup      { left: 6.00%; top:61.18%; width:256px; }
.ic--automation    { left:29.85%; top:74.52%; width:268px; }
.ic--audit         { left:57.54%; top:64.54%; width:172px;  }
.ic--appstore      { left:69.85%; top:49.64%; width:137px;  }
.ic--playstore     { left:69.92%; top:56.37%; width:154px;  }

// ─────────────────────────────────────────────────────────────
// Centre logo circle
// Figma: Ellipse4 145 px circle centred at ~(325, 416.5)
// which is essentially 50%, 50%
// ─────────────────────────────────────────────────────────────
.illustration-logo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
}

// White circle — Figma's Ellipse4 (≈145 px)
.illustration-logo__circle {
  position: relative;
  z-index: 2;
  width: 145px;
  height: 145px;
  border-radius: 50%;
  /* Ellipse 4 */
  background: radial-gradient(72.08% 64.07% at 32.5% 40%, rgba(211, 236, 245, 0.5) 0%, rgba(211, 220, 247, 0.5) 66.58%, rgba(210, 208, 248, 0.5) 93.63%, rgba(193, 190, 255, 0.5) 98.56%) /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 16px;
  overflow: hidden;
  box-shadow:
    0 0 20.4px 20.4px rgba(255,255,255);
}


// Logo mark
.illustration-logo__img {
  width: 68px;
  height: auto;
  object-fit: contain;
  flex-shrink: 0;
}
</style>